import styled, { keyframes } from 'styled-components'
import Flyout from 'common-components/Flyout'
import { COLORS } from 'consts/colors'
import { customizeVerticalScrollbarThumbMixin } from 'styles/mixins'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'

// Reveal the flyout only after it has been measured and positioned.
const flyoutReveal = keyframes`
  from {
    visibility: hidden;
  }
  to {
    visibility: visible;
  }
`

// Wraps the shared Flyout (whose className is forwarded to its positioned wrapper)
// without modifying it. Fixes the open "flash from the top-left corner": the wrapper
// starts at {0,0} before it's measured, so we (1) drop the top/left transition so it
// never animates in from the corner, and (2) keep it hidden for the first few frames
// until it has real coordinates, then reveal it.
export const PositionedFlyout = styled(Flyout)`
  transition: none !important;
  animation: ${flyoutReveal} 0.01s linear 0.06s both;
`

export const Panel = styled.div`
  width: 380px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* no right padding so the scroll container (and its scrollbar) reaches the panel edge */
  padding: 16px 0 16px 16px;
  background-color: ${COLORS.white};
  border-radius: 4px;
  @media screen and (max-width: ${SMALL_DESKTOP_SIZE_BREAKPOINT}px) {
    /* no top padding so the sticky Header can sit flush at the scroller's top
       edge — otherwise a thumbnail row peeks through the gap above it (the
       Header carries its own top padding instead). */
    padding: 0 12px 12px;
    width: 100%;
    max-width: none;
    /* On mobile the panel itself is the scroller inside the SlideUp sheet, while the
       grid below flows at its natural height (see ScrollArea). We CAP the panel to the
       sheet's inner height instead of flex-growing it: the SlideUp sheet reserves
       ~148px (its max-height) plus its drag handle + padding, so ~180px total. Capping
       (rather than flex: 1) keeps the flex column from shrinking the SlideUp drag
       handle, and keeps the grid itself unconstrained so its rows aren't squashed. */
    min-height: 0;
    max-height: calc(100dvh - 180px);
    overflow-y: auto;
    ${customizeVerticalScrollbarThumbMixin()};
    gap: 0px;
  }
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  /* match the grid's right inset so the toggle aligns with the thumbnails */
  padding-right: 16px;
  @media screen and (max-width: ${SMALL_DESKTOP_SIZE_BREAKPOINT}px) {
    /* On mobile the Panel itself scrolls (see Panel), so pin the header flush to
       the top of that scroller. White background + z-index cover the thumbnails
       scrolling underneath; the Panel drops its top padding so nothing peeks
       above, and the header restores that 12px gap via its own padding-top. */
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${COLORS.white};
    padding-right: 0;
    padding-top: 12px;
    padding-bottom: 16px;
  }
`

export const ScrollArea = styled.div`
  /* cap at the design height, but shrink with the viewport on short screens so the
     panel never grows taller than the screen and gets shifted off the top edge */
  max-height: min(624px, calc(100dvh - 150px));
  overflow-y: auto;
  display: grid;
  /* responsive columns: fills as many as fit so it never collapses to one column on mobile */
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  /* keep rows at the item's aspect-ratio height instead of stretching to fill */
  align-content: start;
  gap: 12px;
  /* keeps the grid inset from the right while the scrollbar sits at the panel edge */
  padding-right: 16px;
  ${customizeVerticalScrollbarThumbMixin()}
  @media screen and (max-width: ${SMALL_DESKTOP_SIZE_BREAKPOINT}px) {
    /* the panel scrolls on mobile; the grid keeps its natural height (flex: none so
       flex can never shrink its aspect-ratio rows) and just lays out its thumbnails */
    flex: none;
    max-height: none;
    overflow: visible;
    padding-right: 0;
  }
`

export const PresetItem = styled.div<{ $isSelected: boolean; $ratio: number }>`
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  box-sizing: border-box;
  background-color: ${COLORS.secondaryUltraLight};
  /* fill the grid column instead of a fixed width so items scale down on small screens */
  width: 100%;
  /* Cell shape follows the project aspect ratio (landscape for 16:9, portrait for 9:16).
     Safari doesn't reliably resolve aspect-ratio height for a grid item in an auto-sized
     row, collapsing the cell — so we drive the height with the classic padding-top ratio
     hack ($ratio is width/height, so height-as-%-of-width is 100/$ratio) and fill it with
     an absolutely positioned image. */
  height: 0;
  padding-top: ${({ $ratio }) => `${100 / $ratio}%`};

  /* Selection ring painted ABOVE the image (the image fills the cell at inset:0, so an
     outline on the cell itself would be hidden underneath it). Inset border keeps the
     ring within the rounded, clipped cell. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid
      ${({ $isSelected }) => ($isSelected ? COLORS.primaryDark : 'transparent')};
    border-radius: 6px;
    pointer-events: none;
    transition: border-color 0.15s ease;
  }

  &:hover::after {
    border-color: ${COLORS.primaryDark};
  }
`

export const PresetImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 4px;
`

export const LoaderArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
`
